import { useId } from 'react'
import type { Interval } from '@/shared/lib'
import { addDays, fmtDuration, startOfDay, toISODate } from '@/shared/lib'
import { Input } from '@/shared/ui/field'

interface IntervalFieldProps {
  value: Interval
  onChange: (next: Interval) => void
}

function timeValue(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function withTime(day: Date, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number)
  const next = startOfDay(day)
  next.setHours(h ?? 0, m ?? 0, 0, 0)
  return next
}

/**
 * Дата + два времени. Конец не позже начала означает ночной выход, а не ошибку
 * ввода: экскурсии по крышам заканчиваются после полуночи, поэтому конец
 * автоматически переезжает на следующие сутки.
 */
export function IntervalField({ value, onChange }: IntervalFieldProps) {
  const id = useId()

  const setDate = (iso: string) => {
    const day = new Date(`${iso}T00:00:00`)
    if (Number.isNaN(day.getTime())) return
    const duration = value.endsAt.getTime() - value.startsAt.getTime()
    const startsAt = withTime(day, timeValue(value.startsAt))
    onChange({ startsAt, endsAt: new Date(startsAt.getTime() + duration) })
  }

  const setStart = (hhmm: string) => {
    const duration = value.endsAt.getTime() - value.startsAt.getTime()
    const startsAt = withTime(value.startsAt, hhmm)
    onChange({ startsAt, endsAt: new Date(startsAt.getTime() + duration) })
  }

  const setEnd = (hhmm: string) => {
    let endsAt = withTime(value.startsAt, hhmm)
    if (endsAt <= value.startsAt) endsAt = addDays(endsAt, 1)
    onChange({ startsAt: value.startsAt, endsAt })
  }

  const overnight = toISODate(value.startsAt) !== toISODate(value.endsAt)

  return (
    <div className="flex flex-col gap-2">
      <span className="label">Когда</span>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={id}
          type="date"
          aria-label="Дата"
          className="w-auto"
          value={toISODate(value.startsAt)}
          onChange={(e) => setDate(e.target.value)}
        />
        <Input
          type="time"
          aria-label="Начало"
          step={900}
          className="w-auto"
          value={timeValue(value.startsAt)}
          onChange={(e) => setStart(e.target.value)}
        />
        <span aria-hidden className="text-text-tertiary">
          –
        </span>
        <Input
          type="time"
          aria-label="Конец"
          step={900}
          className="w-auto"
          value={timeValue(value.endsAt)}
          onChange={(e) => setEnd(e.target.value)}
        />
      </div>
      <span className="text-xs text-text-tertiary">
        {fmtDuration(value)}
        {overnight && ' · заканчивается на следующий день'}
      </span>
    </div>
  )
}
